import { useState, type FormEvent } from 'react';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于久久热',
    description: '久久热是您发现热门内容、追踪流行趋势的首选平台。了解久久热的品牌故事、用户口碑和联系方式。',
    url: 'https://mosquitos.cn/about',
    isPartOf: {
      '@type': 'WebSite',
      name: '久久热',
      url: 'https://mosquitos.cn',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '久久热有哪些内容频道？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '久久热目前涵盖娱乐、资讯、生活、科技、体育、时尚六大内容频道，每天持续更新热门内容。',
        },
      },
      {
        '@type': 'Question',
        name: '如何向久久热投稿或提供内容建议？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过本页面的联系表单向我们提交内容建议或合作意向，我们的编辑团队会认真对待每一条反馈。',
        },
      },
      {
        '@type': 'Question',
        name: '久久热的内容更新频率是怎样的？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '久久热坚持每日更新，确保用户每天都能看到最新、最热门的优质内容。',
        },
      },
      {
        '@type': 'Question',
        name: '久久热是否免费使用？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '是的，久久热对用户完全免费开放，您无需注册即可浏览所有内容。',
        },
      },
    ],
  };

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '关于我们' },
  ];

  const reputations = [
    {
      name: '张明',
      role: '资深媒体人',
      text: '久久热已经成为我每天必看的内容平台。信息更新快、内容质量高，推荐算法也很精准，总能给我推荐真正感兴趣的内容。',
      avatar: 'https://readdy.ai/api/search-image?query=Professional%20Chinese%20man%20portrait%20in%20his%2040s%2C%20warm%20friendly%20smile%2C%20simple%20solid%20light%20background%2C%20professional%20headshot%2C%20clean%20minimalist%20style&width=120&height=120&seq=avatar-zm&orientation=squarish',
    },
    {
      name: '李婷',
      role: '互联网从业者',
      text: '作为一个每天都要追踪行业动态的人，久久热的科技频道就是我的信息雷达。分类清晰，文章有深度，非常好用。',
      avatar: 'https://readdy.ai/api/search-image?query=Professional%20Chinese%20woman%20portrait%20in%20her%2030s%2C%20confident%20friendly%20smile%2C%20simple%20solid%20light%20background%2C%20professional%20headshot%2C%20clean%20minimalist%20style&width=120&height=120&seq=avatar-lt&orientation=squarish',
    },
    {
      name: '王浩',
      role: '大学生',
      text: '同学推荐的这个平台，用了一段时间真的爱上了。内容丰富又有趣，课余时间刷刷久久热已经成为习惯。',
      avatar: 'https://readdy.ai/api/search-image?query=Young%20Chinese%20male%20student%20portrait%20in%20his%20early%2020s%2C%20warm%20friendly%20smile%2C%20simple%20solid%20light%20background%2C%20casual%20headshot%2C%20clean%20minimalist%20style&width=120&height=120&seq=avatar-wh&orientation=squarish',
    },
  ];

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypotEl = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotEl && honeypotEl.value.trim() !== '') {
      setFormStatus('success');
      setFormMessage('留言发送成功！我们会尽快回复您。');
      form.reset();
      return;
    }

    setFormStatus('submitting');
    setFormMessage('');

    try {
      const formData = new FormData(form);
      if (honeypotEl) {
        formData.delete('phone_alt');
      }

      const response = await fetch('https://readdy.ai/api/form/d9rdtsue3oq4jqumu080', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (
        response.ok &&
        parsed &&
        (parsed.code === 'OK' || parsed.code === 'ok')
      ) {
        setFormStatus('success');
        setFormMessage('留言发送成功！我们会尽快回复您。');
        form.reset();
      } else if (
        serverMsg &&
        (String(serverMsg).toLowerCase().includes('spam') || String(serverMsg).includes('form data is spam'))
      ) {
        setFormStatus('error');
        setFormMessage('发送失败，请稍后重试。');
      } else {
        setFormStatus('error');
        setFormMessage(typeof serverMsg === 'string' ? serverMsg : '发送失败，请稍后重试。');
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络异常，请检查连接后重试。');
    }
  };

  return (
    <>
      <SEOHead
        title="关于我们"
        description="了解久久热的品牌故事、用户口碑和联系方式。久久热是您发现热门内容、追踪流行趋势的首选平台。"
        keywords="久久热关于我们,久久热品牌,久久热联系方式,内容聚合平台"
        canonicalUrl="https://mosquitos.cn/about"
        schemaJson={[aboutSchema, faqSchema]}
      />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <Breadcrumb items={breadcrumbItems} />
          <div className="w-full px-4 md:px-6 pb-6">
            <div className="max-w-7xl mx-auto">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mt-2">
                关于我们
              </h1>
              <p className="mt-2 text-sm text-foreground-600">
                了解久久热的故事与愿景
              </p>
            </div>
          </div>
        </div>

        <section className="w-full py-12 md:py-16 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row items-center gap-10 md:gap-14">
              <div className="flex-1">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-5">
                  品牌故事
                </h2>
                <p className="text-foreground-700 leading-relaxed mb-4">
                  久久热诞生于对优质内容的热爱。我们相信，在这个信息爆炸的时代，每个人都值得看到最精彩的内容。
                </p>
                <p className="text-foreground-700 leading-relaxed mb-4">
                  自创立以来，久久热始终坚持<strong>"内容为王"</strong>的核心理念，致力于打造一个汇聚热门资讯、传递正能量的内容平台。
                </p>
                <p className="text-foreground-700 leading-relaxed">
                  我们的编辑团队来自各大主流媒体，拥有丰富的行业经验和敏锐的内容嗅觉，24小时不间断为您筛选最值得关注的热点内容。无论是娱乐八卦、科技前沿、生活百科还是体育赛事，久久热都力求为您呈现最精彩的一面。
                </p>
              </div>
              <div className="flex-1">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20creative%20office%20workspace%20with%20team%20collaboration%2C%20warm%20ambient%20lighting%2C%20natural%20wood%20desk%2C%20large%20monitors%20displaying%20content%2C%20professional%20editorial%20team%20atmosphere%2C%20clean%20minimalist%20design%2C%20harmonious%20composition&width=700&height=500&seq=about-brand&orientation=landscape"
                  alt="久久热团队工作场景"
                  className="w-full rounded-xl object-cover aspect-[4/3]"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="w-full py-12 md:py-16 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                用户口碑
              </h2>
              <p className="mt-2 text-sm text-foreground-600">
                听听用户们怎么说
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {reputations.map((rep, index) => (
                <div
                  key={index}
                  className="bg-background-50 rounded-xl border border-background-200/70 p-6 flex flex-col items-center text-center"
                >
                  <img
                    src={rep.avatar}
                    alt={rep.name}
                    className="w-16 h-16 rounded-full object-cover mb-4"
                  />
                  <p className="text-foreground-700 text-sm leading-relaxed mb-4">
                    &ldquo;{rep.text}&rdquo;
                  </p>
                  <div>
                    <p className="text-sm font-semibold text-foreground-900">{rep.name}</p>
                    <p className="text-xs text-foreground-500">{rep.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full py-12 md:py-16 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-10 md:gap-14">
              <div className="flex-1">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-5">
                  联系我们
                </h2>
                <p className="text-foreground-700 leading-relaxed mb-6">
                  如果您有任何建议、合作意向或内容投稿，欢迎通过以下方式联系我们。久久热期待与您的每一次交流。
                </p>

                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <span className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-accent-600 text-lg" />
                    </span>
                    <div>
                      <p className="text-xs text-foreground-500">地址</p>
                      <p className="text-sm text-foreground-800">中国上海市浦东新区张江高科技园区</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-accent-600 text-lg" />
                    </span>
                    <div>
                      <p className="text-xs text-foreground-500">电话</p>
                      <p className="text-sm text-foreground-800">+86 400-888-9999</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-accent-600 text-lg" />
                    </span>
                    <div>
                      <p className="text-xs text-foreground-500">邮箱</p>
                      <p className="text-sm text-foreground-800">contact@mosquitos.cn</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex-1">
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  className="bg-background-100 rounded-xl border border-background-200/70 p-6 md:p-8"
                >
                  <h3 className="font-heading text-lg font-bold text-foreground-900 mb-6">
                    发送留言
                  </h3>

                  <div className="space-y-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        您的姓名
                      </label>
                      <input
                        type="text"
                        id="contact-name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-300/60 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>

                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        电子邮箱
                      </label>
                      <input
                        type="email"
                        id="contact-email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-300/60 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                        placeholder="请输入您的电子邮箱"
                      />
                    </div>

                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        留言内容
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-300/60 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors resize-none"
                        placeholder="请输入您的留言内容（500字以内）"
                      />
                    </div>

                    <div className="honeypot-field">
                      <input
                        type="text"
                        name="phone_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={formStatus === 'submitting'}
                      className="w-full py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-white font-semibold rounded-lg text-sm transition-all duration-200 whitespace-nowrap cursor-pointer"
                    >
                      {formStatus === 'submitting' ? '发送中...' : '发送留言'}
                    </button>

                    {formStatus === 'success' && (
                      <p className="text-sm text-accent-600 text-center font-medium">{formMessage}</p>
                    )}
                    {formStatus === 'error' && (
                      <p className="text-sm text-red-500 text-center font-medium">{formMessage}</p>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full py-12 md:py-16 px-4 md:px-6 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                常见问题
              </h2>
            </div>

            <div className="space-y-4">
              {[
                {
                  q: '久久热有哪些内容频道？',
                  a: '久久热目前涵盖娱乐、资讯、生活、科技、体育、时尚六大内容频道，每天持续更新热门内容。',
                },
                {
                  q: '如何向久久热投稿或提供内容建议？',
                  a: '您可以通过本页面的联系表单向我们提交内容建议或合作意向，我们的编辑团队会认真对待每一条反馈。',
                },
                {
                  q: '久久热的内容更新频率是怎样的？',
                  a: '久久热坚持每日更新，确保用户每天都能看到最新、最热门的优质内容。',
                },
                {
                  q: '久久热是否免费使用？',
                  a: '是的，久久热对用户完全免费开放，您无需注册即可浏览所有内容。',
                },
              ].map((faq, index) => (
                <details
                  key={index}
                  className="bg-background-50 rounded-xl border border-background-200/70 group"
                >
                  <summary className="px-6 py-4 text-sm font-semibold text-foreground-800 cursor-pointer flex items-center justify-between list-none">
                    {faq.q}
                    <i className="ri-add-line text-foreground-400 group-open:hidden" />
                    <i className="ri-subtract-line text-foreground-400 hidden group-open:block" />
                  </summary>
                  <div className="px-6 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}